/**
 * slotColumns — what the slot table shows, and in what order it shows it.
 *
 * Pure functions, deliberately separate from SlotEditorTab: the comparators are
 * where this feature is actually wrong or right, and they are worth testing
 * without mounting a component that also does bulk deletes.
 *
 * TWO RULES WORTH KNOWING BEFORE EDITING
 *
 * 1. Sorting is DISPLAY-KEYED, not field-keyed. Three columns render something
 *    other than the raw field, and sorting the raw field would look broken:
 *    Time renders `formatTime12Hour` ("1:00 PM") but must sort on the 24-hour
 *    `startTime`, or 10 AM lands after 1 PM. Student renders `studentName` OR
 *    `holdStudentName` — sorting only the first would scatter all 35 carryover
 *    holds. Status renders a word but sorts on a RANK, because alphabetical
 *    gives assigned/blocked/open/provisional, which is not a thing anyone means.
 *
 * 2. Every sort keeps day-then-time as a tiebreak, ascending, ALWAYS — including
 *    under a descending primary. "Sort by Status" should read as grouped and
 *    still in schedule order; without the tiebreak the rows inside each group
 *    land in whatever order the previous sort left them, which reads as a bug.
 */

import { isSingleDate } from './slotKind';

// Columns the picker can hide, in table order.
//
// Select / Day / Time / Actions are deliberately absent: Day and Time are the
// row's identity (hide both and the rows are indistinguishable), and the other
// two are controls rather than data.
export const SLOT_COLUMNS = [
	{ key: 'status', label: 'Status' },
	{ key: 'duration', label: 'Duration' },
	{ key: 'instructor', label: 'Instructor' },
	{ key: 'location', label: 'Location' },
	{ key: 'student', label: 'Student' },
];

// Instructor is off by default because it is empty: 0 of the 63 live fall slots
// carry one, and that is by design — a timeSlot holds a student's recurring
// weekly time, and teachers rotate through it per occurrence (the rotation lives
// in scheduleAssignments). The column renders 63 em-dashes. It stays in the
// catalog rather than being deleted because the field is still real and still
// settable in the create/edit form.
export const DEFAULT_SLOT_COLUMNS = ['status', 'duration', 'location', 'student'];

export const DEFAULT_SLOT_SORT = { key: 'day', direction: 'asc' };

// Open first, then the ones still in flight, then settled, then unavailable —
// which is also the order they get worked during carryover.
const STATUS_RANK = { open: 0, provisional: 1, assigned: 2, blocked: 3 };

// The value each sortable column sorts ON, which is not always the field it
// renders — see rule 1 above.
const SORT_VALUE = {
	status: (s) => STATUS_RANK[s.status] ?? 99,
	day: (s) => dayKey(s),
	time: (s) => s.startTime,
	duration: (s) => s.duration,
	instructor: (s) => s.instructor,
	location: (s) => s.location,
	student: (s) => s.studentName || s.holdStudentName,
};

export const isSortable = (key) => Object.prototype.hasOwnProperty.call(SORT_VALUE, key);

const isBlank = (v) => v === null || v === undefined || v === '';

const compare = (a, b) =>
	typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));

// Weekly slots by weekday, THEN single-date slots by calendar date. A makeup
// doc has no dayOfWeek, so `a.dayOfWeek - b.dayOfWeek` was NaN for it and the
// makeups landed wherever the engine left them; and sorting them by weekday
// would interleave Oct 30 with Nov 6, which is not the order anyone books them.
const dayKey = (s) => (isSingleDate(s) ? `1-${s.specificDate}` : `0-${s.dayOfWeek}`);

const tiebreak = (a, b) =>
	dayKey(a).localeCompare(dayKey(b)) || String(a.startTime).localeCompare(String(b.startTime));

/**
 * Sort a copy of `slots`. Never mutates — the caller's array is the fetch
 * result and other derivations (counts, selection) read it independently.
 *
 * Blanks always sink to the bottom, in BOTH directions. An empty Instructor or
 * an unnamed slot is an absence, not a value: flipping to descending should
 * reverse the rows that have data, not float the 63 empty ones to the top.
 */
export function sortSlots(slots, sort = DEFAULT_SLOT_SORT) {
	const getValue = SORT_VALUE[sort?.key];
	const dir = sort?.direction === 'desc' ? -1 : 1;

	return [...slots].sort((a, b) => {
		if (getValue) {
			const av = getValue(a);
			const bv = getValue(b);
			const aBlank = isBlank(av);
			const bBlank = isBlank(bv);
			if (aBlank || bBlank) {
				if (!(aBlank && bBlank)) return aBlank ? 1 : -1;
			} else {
				const c = compare(av, bv);
				if (c !== 0) return c * dir;
			}
		}
		return tiebreak(a, b);
	});
}

/**
 * Next sort state for a header click: same column flips direction, a new column
 * starts ascending.
 */
export function nextSort(current, key) {
	return {
		key,
		direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc',
	};
}

/**
 * Reconcile a stored column selection against the catalog.
 *
 * Absent (never chosen) means defaults; `[]` means deliberately empty and must
 * survive — collapsing the two would make it impossible to turn everything off,
 * because the defaults would keep coming back. Same distinction as
 * normalizeDashboardCards, and for the same reason: the doc is user-writable and
 * outlives the code that wrote it, so unknown keys are dropped on read.
 */
export function normalizeSlotColumns(stored, catalog = SLOT_COLUMNS) {
	const allowed = new Set(catalog.map((c) => c.key));
	if (!Array.isArray(stored)) return DEFAULT_SLOT_COLUMNS.filter((k) => allowed.has(k));
	const seen = new Set();
	return stored.filter((k) => {
		if (!allowed.has(k) || seen.has(k)) return false;
		seen.add(k);
		return true;
	});
}

/**
 * The `is-hidden--<key>` classes for the table element.
 *
 * Hiding is CSS gated to >=641px, NOT dropping cells from the DOM. Below that
 * breakpoint the slot table reflows into self-labeling stacked cards
 * (Scheduling.css) which must show every field regardless of picker state — an
 * unrendered <td> would vanish from those too. Same call as Inquiries.js, and
 * the same reason.
 */
export function hiddenColumnClasses(selected, catalog = SLOT_COLUMNS) {
	const shown = new Set(selected);
	return catalog
		.filter((c) => !shown.has(c.key))
		.map((c) => `is-hidden--${c.key}`)
		.join(' ');
}
